import { useState } from "react";
import type { DatabaseActionType } from "../../../../core/modules/Database/databaseTypes";
import { Button } from "@/components/ui/button";
import { GenericApiOkResp } from "@shared/genericApiTypes";
import { useAdminPerms } from "@/hooks/auth";
import { Loader2Icon, Trash2Icon } from "lucide-react";
import { useBackendApi } from "@/hooks/fetch";
import { useCloseActionModal } from "@/hooks/actionModal";
import { useRefreshPlayerModal } from "@/hooks/playerModal";
import { useOpenConfirmDialog } from "@/hooks/dialogs";
import { useContentRefresh } from "@/hooks/pages";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { ApiDeleteActionReqSchema, ApiRevokeActionReqSchema } from "../../../../core/routes/history/actions";


type ActionModifyTabProps = {
    action: DatabaseActionType;
    refreshModalData: () => void;
}

export default function ActionModifyTab({ action, refreshModalData }: ActionModifyTabProps) {
    const [isRevoking, setIsRevoking] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const { hasPerm } = useAdminPerms();
    const openConfirmDialog = useOpenConfirmDialog();
    const closeActionModal = useCloseActionModal();
    const refreshContent = useContentRefresh();
    const refreshPlayerModal = useRefreshPlayerModal();
    const revokeActionApi = useBackendApi<GenericApiOkResp, ApiRevokeActionReqSchema>({
        method: 'POST',
        path: `/history/revokeAction`,
    });
    const deleteActionApi = useBackendApi<GenericApiOkResp, ApiDeleteActionReqSchema>({
        method: 'POST',
        path: `/history/deleteAction`,
    });

    const upperCasedType = action.type.charAt(0).toUpperCase() + action.type.slice(1);
    const doRevokeAction = () => {
        setIsRevoking(true);
        revokeActionApi({
            data: { actionId: action.id },
            toastLoadingMessage: `Revoking ${action.type}...`,
            genericHandler: {
                successMsg: `${upperCasedType} revoked.`,
            },
            success: (data) => {
                setIsRevoking(false);
                if ('success' in data) {
                    refreshModalData();
                    refreshPlayerModal();
                    refreshContent();
                }
            },
        });
    }

    const doDeleteAction = () => {
        openConfirmDialog({
            title: `Delete ${upperCasedType}`,
            actionLabel: 'Delete forever',
            confirmBtnVariant: 'destructive',
            message: <>
                This removes the {action.type} from the database for good. There is no undo,
                and the entry will be gone from the player history as if it never happened.
            </>,
            onConfirm: () => {
                setIsDeleting(true);
                deleteActionApi({
                    data: { actionId: action.id },
                    toastLoadingMessage: `Deleting ${action.type}...`,
                    genericHandler: {
                        successMsg: `${upperCasedType} deleted.`,
                    },
                    success: (data) => {
                        setIsDeleting(false);
                        if ('success' in data) {
                            closeActionModal();
                            refreshPlayerModal();
                            refreshContent();
                        }
                    },
                });
            },
        });
    }

    const isAlreadyRevoked = !!action.revocation.timestamp;
    const revokePermMap = { warn: 'players.warn', jail: 'players.jail', ban: 'players.ban' } as const;
    const isAnticheatAction = (action.type === 'ban' || action.type === 'jail') && !!action.anticheat;
    const hasRevokePerm = hasPerm(revokePermMap[action.type])
        && (!isAnticheatAction || hasPerm('players.anticheat'));
    const isRevokeBlocked = isAlreadyRevoked || !hasRevokePerm;
    //GMW: this core has no route behind the delete button, and `players.clear_history`
    //is not a permission it knows - so a master account would otherwise see a button
    //that only ever errors. Flip this to true once the backend route exists.
    const CAN_DELETE_ACTIONS = false;
    const canDelete = CAN_DELETE_ACTIONS && hasPerm('players.clear_history') && hasRevokePerm;
    //the only non-obvious reason for a blocked revoke, so it gets a hover hint
    const anticheatBlocked = isAnticheatAction && !isAlreadyRevoked && !hasPerm('players.anticheat');
    const revokeBtnLabel = isAlreadyRevoked
        ? `${action.type} revoked`
        : hasRevokePerm
            ? `Revoke ${upperCasedType}`
            : 'Revoke (no permission)';
    return (
        <div className="flex flex-col gap-4 px-1 mb-1 md:mb-4">
            <div className="space-y-2">
                <h3 className="text-xl">Revoke {upperCasedType}</h3>
                <p className="text-muted-foreground text-sm">
                    This is generally done when the player successfully appeals the {action.type} or the admin regrets issuing it.
                    <ul className="list-disc list-inside pt-1">
                        {action.type === 'ban' && <li>The player will be able to rejoin the server.</li>}
                        {action.type === 'jail' && <li>The admin jail will no longer be listed as active.</li>}
                        <li>The player will not be notified of the revocation.</li>
                        <li>This {action.type} will not be removed from the player history.</li>
                        <li>The revocation cannot be undone.</li>
                    </ul>
                </p>

                <Tooltip>
                    <TooltipTrigger asChild>
                        <span
                            tabIndex={anticheatBlocked ? 0 : -1}
                            className={isRevokeBlocked ? 'inline-block cursor-not-allowed' : 'inline-block'}
                        >
                            <Button
                                variant="destructive"
                                size='xs'
                                className={cn(
                                    "col-start-1 col-span-full xs:col-span-3 xs:col-start-2",
                                    isRevokeBlocked && 'pointer-events-none',
                                )}
                                type="submit"
                                disabled={isRevokeBlocked || isRevoking}
                                onClick={doRevokeAction}
                            >
                                {isRevoking ? (
                                    <span className="flex items-center leading-relaxed">
                                        <Loader2Icon className="inline animate-spin h-4" /> Revoking...
                                    </span>
                                ) : revokeBtnLabel}
                            </Button>
                        </span>
                    </TooltipTrigger>
                    {anticheatBlocked ? (
                        <TooltipContent className="text-destructive-inline text-center">
                            <p>
                                This is an anticheat {action.type === 'ban' ? 'ban' : 'admin jail'}.<br />
                                You need the "Anticheat Sanctions" permission to revoke it.
                            </p>
                        </TooltipContent>
                    ) : null}
                </Tooltip>
            </div>

            {canDelete ? (
                <div className="space-y-2 border-t border-edge pt-4">
                    <h3 className="text-xl">Delete {upperCasedType}</h3>
                    <p className="text-muted-foreground text-sm">
                        Use this when the {action.type} should never have been issued in the first place.
                        <ul className="list-disc list-inside pt-1">
                            <li>The {action.type} is erased from the database, not just marked as revoked.</li>
                            <li>It disappears from the player history and from the sanction counts.</li>
                            <li>This cannot be undone.</li>
                        </ul>
                    </p>

                    <Button
                        variant="destructive"
                        size='xs'
                        disabled={isDeleting}
                        onClick={doDeleteAction}
                    >
                        {isDeleting ? (
                            <span className="flex items-center leading-relaxed">
                                <Loader2Icon className="inline animate-spin h-4" /> Deleting...
                            </span>
                        ) : (
                            <span className="flex items-center leading-relaxed">
                                <Trash2Icon className="inline h-4 mr-1" /> Delete from History
                            </span>
                        )}
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
